/**
 * DHIQUEST header behaviour.
 * - Mobile menu button opens/closes the navigation drawer.
 * - Dropdowns (<details>) close on outside click, on Escape, and when another opens (desktop).
 * - Placeholder links (data-dq-placeholder), anywhere on the page, do nothing until a real URL is set.
 * Re-initialises when the section is reloaded in the theme editor.
 */
(() => {
  const DESKTOP = window.matchMedia('(min-width: 1100px)');
  const controllers = new WeakMap();

  function init(header) {
    if (controllers.has(header)) return;
    const controller = new AbortController();
    const { signal } = controller;
    controllers.set(header, controller);

    const toggle = header.querySelector('[data-dq-menu-toggle]');
    const disclosures = Array.from(header.querySelectorAll('[data-dq-disclosure]'));

    const setDrawer = (open) => {
      if (!toggle) return;
      if (open) {
        header.style.setProperty('--dq-drawer-top', `${Math.max(0, header.getBoundingClientRect().bottom)}px`);
      }
      header.toggleAttribute('data-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      document.documentElement.classList.toggle('dq-scroll-lock', open);
    };

    const closeDisclosures = (except) => {
      disclosures.forEach((d) => {
        if (d !== except) d.open = false;
      });
    };

    toggle?.addEventListener('click', () => setDrawer(!header.hasAttribute('data-open')), { signal });

    disclosures.forEach((d) => {
      d.addEventListener(
        'toggle',
        () => {
          if (d.open && DESKTOP.matches) closeDisclosures(d);
        },
        { signal }
      );
    });

    document.addEventListener(
      'click',
      (event) => {
        if (DESKTOP.matches) closeDisclosures(event.target.closest?.('[data-dq-disclosure]'));
      },
      { signal }
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        const open = disclosures.find((d) => d.open);
        if (open) {
          open.open = false;
          open.querySelector('summary')?.focus();
        } else if (header.hasAttribute('data-open')) {
          setDrawer(false);
          toggle?.focus();
        }
      },
      { signal }
    );

    DESKTOP.addEventListener(
      'change',
      () => {
        setDrawer(false);
        closeDisclosures();
      },
      { signal }
    );
  }

  function destroy(header) {
    controllers.get(header)?.abort();
    controllers.delete(header);
    document.documentElement.classList.remove('dq-scroll-lock');
  }

  const initAll = (root = document) => root.querySelectorAll('[data-dq-header]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest?.('a[data-dq-placeholder]')) event.preventDefault();
  });

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-dq-header]').forEach(destroy);
  });
})();
